import { useCallback, useEffect, useState } from "react";
import { RefreshCw, Sparkles } from "lucide-react";
import { API } from "../lib/api";

const at = (s) => new Date(s).toLocaleTimeString("en-IN", { timeZone: "Asia/Kolkata", timeStyle: "short" });

const readStream = async (res, on) => {
  const reader = res.body.getReader();
  const dec = new TextDecoder();
  let buf = "";
  for (;;) {
    const { value, done } = await reader.read();
    if (done) return;
    buf += dec.decode(value, { stream: true });
    const parts = buf.split("\n\n");
    buf = parts.pop();
    parts.filter((p) => p.startsWith("data: ")).forEach((p) => on(JSON.parse(p.slice(6))));
  }
};

const useInsights = () => {
  const [s, setS] = useState({ text: "", busy: false, err: "", at: null });
  const run = useCallback(async (fresh, signal) => {
    setS({ text: "", busy: true, err: "", at: null });
    try {
      const res = await fetch(`${API}/brief/insights${fresh ? "?fresh=true" : ""}`, { credentials: "include", signal });
      if (!res.ok) throw new Error(res.status === 401 ? "Your session has ended. Please sign in again." : "Insights are unavailable right now.");
      await readStream(res, (ev) => setS((x) => ({ ...x, text: x.text + (ev.t || ""), err: ev.error || x.err, at: ev.at || x.at })));
      setS((x) => ({ ...x, busy: false }));
    } catch (e) {
      if (e.name !== "AbortError") setS((x) => ({ ...x, busy: false, err: e.message }));
    }
  }, []);
  return [s, run];
};

export const BriefInsights = () => {
  const [s, run] = useInsights();
  useEffect(() => {
    const c = new AbortController();
    run(false, c.signal);
    return () => c.abort();
  }, [run]);
  const lines = s.text.split("\n").map((l) => l.replace(/^\s*[-•*\d.)]+\s*/, "").trim()).filter(Boolean);
  return (
    <div className="ax-panel p-4 mb-4 border-l-4 border-l-primary" data-testid="brief-insights">
      <div className="flex flex-wrap items-center gap-2 mb-3">
        <Sparkles className="h-4 w-4 text-primary" /><span className="font-semibold text-sm">Insights to focus on</span>
        <span className="text-xs text-muted-foreground" data-testid="brief-insights-status">{s.busy ? "Reading today's numbers…" : s.at ? `Written by AI at ${at(s.at)}` : ""}</span>
        <button data-testid="brief-insights-refresh" disabled={s.busy} onClick={() => run(true)} className="ml-auto border h-8 px-3 text-xs flex items-center gap-1.5 hover:border-primary disabled:opacity-50">
          <RefreshCw className={`h-3.5 w-3.5 ${s.busy ? "animate-spin" : ""}`} />Rewrite</button>
      </div>
      {lines.length > 0 && <ol className="space-y-2.5" data-testid="brief-insights-list">{lines.map((l, i) => (
        <li key={i} className="flex gap-3 text-sm leading-relaxed" data-testid={`brief-insight-${i}`}><span className="num text-primary font-semibold w-4 shrink-0">{i + 1}</span><span>{l}</span></li>))}</ol>}
      {s.busy && !lines.length && <div className="space-y-2" data-testid="brief-insights-loading">{[0, 1, 2].map((i) => <div key={i} className="h-4 bg-muted animate-pulse" style={{ width: `${90 - i * 15}%` }} />)}</div>}
      {s.err && <p className="text-sm text-amber-700 mt-2" data-testid="brief-insights-error">{s.err}</p>}
    </div>
  );
};
